import React, { useState, useEffect } from 'react';
import { fetchSinglePosData } from '../../database/fetch-pos-data';
import { updatepos } from '../../database/set-po-data';
import { successAlert, errorAlert } from '../../components/alerts';

function PaymentStatusModal({ openPaymentModal, setOpenPaymentModal, docId }) {
  const [paymentStatus, setPaymentStatus] = useState('not_paid');
  const [paidAmount, setPaidAmount] = useState('');
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [totalPOInvoice, setTotalPOInvoice] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (openPaymentModal && docId) {
      fetchPaymentData();
    }
  }, [openPaymentModal, docId]);

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      const posData = await fetchSinglePosData(docId);
      
      if (posData) {
        const invoiceStatus = posData.invoiceStatus || 'not_paid';
        const totalInvoice = posData.totalPOInvoice || 0;
        const remaining = posData.remainingAmount || totalInvoice;
        const paid = totalInvoice - remaining;

        setPaymentStatus(invoiceStatus);
        setTotalPOInvoice(totalInvoice);
        setRemainingAmount(remaining);
        setPaidAmount(paid > 0 ? paid.toString() : '');
      }
    } catch (error) {
      console.error('Error fetching payment data:', error);
      errorAlert('Error', 'Failed to fetch payment data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (status) => {
    setPaymentStatus(status);
    
    if (status === 'fully_paid') {
      setPaidAmount(totalPOInvoice.toString());
      setRemainingAmount(0);
    } else if (status === 'not_paid') {
      setPaidAmount('');
      setRemainingAmount(totalPOInvoice);
    } else if (status === 'partially_paid') {
      // Keep current values, user can adjust paid amount
    }
  };

  const handlePaidAmountChange = (amount) => {
    const paid = parseFloat(amount) || 0;
    const remaining = Math.max(0, totalPOInvoice - paid);
    
    setPaidAmount(paid);
    setRemainingAmount(remaining);
    
    // Auto-update status based on amounts
    if (paid >= totalPOInvoice) {
      setPaymentStatus('fully_paid');
    } else if (paid > 0) {
      setPaymentStatus('partially_paid');
    } else {
      setPaymentStatus('not_paid');
    }
  };

  const handlePaidAmountInputChange = (e) => {
    const value = e.target.value;
    // Allow empty string, don't convert to 0 immediately
    if (value === '') {
      setPaidAmount('');
      setRemainingAmount(totalPOInvoice);
      setPaymentStatus('not_paid');
    } else {
      handlePaidAmountChange(value);
    }
  };

  const handleUpdatePaymentStatus = async () => {
    try {
      setLoading(true);
      
      const updateData = {
        invoiceStatus: paymentStatus,
        remainingAmount: remainingAmount,
        paidAmount: paidAmount,
      };

      await updatepos(docId, updateData);
      
      successAlert('Success!', 'Payment status updated successfully.');
      setOpenPaymentModal(false);
    } catch (error) {
      console.error('Error updating payment status:', error);
      errorAlert('Error', 'Failed to update payment status');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  if (!openPaymentModal) return null;

  return (
    <div
      tabIndex="-1"
      aria-hidden="true"
      className="flex items-center justify-center overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 w-full h-[calc(100%)] max-h-full bg-black bg-opacity-50"
    >
      <div className="relative p-4 w-full max-w-md max-h-full">
        <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Payment Status
            </h3>
            <button
              type="button"
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
              onClick={() => setOpenPaymentModal(false)}
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-4 md:p-5 space-y-4">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {/* Total PO Invoice */}
                <div className="text-center">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    Total PO Invoice: {formatCurrency(totalPOInvoice)}
                  </span>
                </div>

                {/* Payment Status */}
                <div>
                  <label className="block mb-3 text-sm font-medium text-gray-900 dark:text-white">
                    Payment Status
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        id="not_paid"
                        type="radio"
                        name="paymentStatus"
                        value="not_paid"
                        checked={paymentStatus === 'not_paid'}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label htmlFor="not_paid" className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                        Not Paid
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="partially_paid"
                        type="radio"
                        name="paymentStatus"
                        value="partially_paid"
                        checked={paymentStatus === 'partially_paid'}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="w-4 h-4 text-yellow-600 bg-gray-100 border-gray-300 focus:ring-yellow-500 dark:focus:ring-yellow-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label htmlFor="partially_paid" className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                        Partially Paid
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="fully_paid"
                        type="radio"
                        name="paymentStatus"
                        value="fully_paid"
                        checked={paymentStatus === 'fully_paid'}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label htmlFor="fully_paid" className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                        Fully Paid
                      </label>
                    </div>
                  </div>
                </div>

                {/* Paid Amount Input (only show for partially paid) */}
                {paymentStatus === 'partially_paid' && (
                  <div>
                    <label htmlFor="paidAmount" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                      How much paid?
                    </label>
                    <input
                      type="number"
                      id="paidAmount"
                      step="0.01"
                      min="0"
                      max={totalPOInvoice}
                      value={paidAmount}
                      onChange={handlePaidAmountInputChange}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      placeholder="Enter paid amount"
                    />
                  </div>
                )}

                {/* Remaining Amount Display */}
                <div className="text-center">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    Remaining Amount: {formatCurrency(remainingAmount)}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 md:p-5 border-t border-gray-200 rounded-b dark:border-gray-600">
            <button
              type="button"
              onClick={() => setOpenPaymentModal(false)}
              className="py-2.5 px-5 ms-3 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpdatePaymentStatus}
              disabled={loading}
              className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Payment Status'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentStatusModal;
